/* eslint-disable @typescript-eslint/no-explicit-any */
import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError, GenericAbortSignal } from 'axios'
import { Loaders } from './types'
import type {
  Dates,
  Item,
  OrderData,
  OrdersData,
  PrintBlankInfo,
  PrintBlankPayload,
  CheckItemsPayload,
} from '~/shared/api/services/delivery/types'
import { DeliveryService } from '~/shared/api/services/delivery'
import {
  KitRelabelConfirmationPayload,
  KitRelabelState,
  KitRelabelStickerPayload,
} from '~/shared/api/services/delivery/types'
import { handleAxiosError } from '~/shared/lib'

export class DeliveryStore {
  currentOrderId: string | null = null

  loaders: Loaders = {
    shippingOrders: false,
    shippingOrderById: false,
    printBlanc: false,
    barcode: false,
    mandatoryMark: false,
    mandatoryMarkReprint: false,
    validateMarks: false,
  }

  shippingOrders: Dates | null = null

  shippingOrderById: OrderData | null = null

  itemsOrderById: Item[] = []

  printBlankInfo: PrintBlankInfo | null = null

  barcode = ''

  inputMandatoryMark = ''

  modalMandatoryMarkField = {
    open: false,
    id: '',
    mandatoryMarkField: '',
  }

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof Loaders) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof Loaders) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  setFieldValue = (name: string, newValue: string | boolean) => {
    const field = this.printBlankInfo?.fields.find((f) => f.name === name)
    if (field && !field.disabled) {
      field.value = newValue
      return
    }

    const additionalField = this.printBlankInfo?.additionalFields.find(
      (addF) => addF.name === name,
    )
    if (additionalField && !additionalField.disabled) {
      additionalField.value = newValue
    }
  }

  setShippingOrders = (shippingOrders: Dates | null) => {
    this.shippingOrders = shippingOrders
  }

  setShippingOrdersFromPayload = (payload?: OrdersData) => {
    this.setShippingOrders(payload?.ordersData?.dates ?? null)
  }

  getShippingOrdersCount = (shippingOrders = this.shippingOrders) => {
    if (!shippingOrders) {
      return 0
    }

    return Object.values(shippingOrders).reduce(
      (count, orders) => count + orders.length,
      0,
    )
  }

  getShippingOrders = async () => {
    this.activeLoader('shippingOrders')
    try {
      const { data } = await DeliveryService.getShippingOrders()
      runInAction(() => {
        this.setShippingOrdersFromPayload(data)
      })
      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.closeLoader('shippingOrders')
      })
    }
  }

  getShippingOrderById = async (id: string, signal?: GenericAbortSignal) => {
    this.currentOrderId = id
    this.activeLoader('shippingOrderById')
    try {
      const { data } = await DeliveryService.getShippingOrderById(id, {
        signal,
      })
      runInAction(() => {
        this.shippingOrderById = data

        const items = data.items.map((item) => {
          return {
            ...item,
            markValue: '',
            checked: false,
          }
        })

        for (const unit of data.detail?.kitRelabel?.units ?? []) {
          if (unit.stickerPrinted || unit.confirmed) {
            for (const productVariationId of unit.componentVariationIds) {
              const item = items.find(
                (candidate) =>
                  !candidate.checked &&
                  candidate.kitVariationId === unit.kitVariationId &&
                  candidate.productVariationId === productVariationId,
              )

              if (item) item.checked = true
            }
          }
        }

        this.itemsOrderById = items
        this.printBlankInfo = data.printBlankInfo
      })
      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.closeLoader('shippingOrderById')
      })
    }
  }

  printBlanc = async (
    dataPrintBlankInfo: PrintBlankInfo,
    orderId: string,
    action: 'downloadBlank' | 'printBlank',
  ) => {
    try {
      this.activeLoader('printBlanc')
      const payload = [
        ...dataPrintBlankInfo.additionalFields,
        ...dataPrintBlankInfo.fields,
      ].reduce(
        (acc, el) => {
          const key = el.name
          acc[key] = el.value === '' ? null : el.value
          return acc
        },
        {} as Record<string, any>,
      )
      payload.mandatoryMarks = this.itemsOrderById.reduce(
        (acc, item) => {
          if (item.mandatoryMarkField && item.markValue) {
            acc[item.mandatoryMarkField] = item.markValue
          }
          return acc
        },
        {} as { [key: string]: string },
      )

      const { data } = await DeliveryService.printBlanc(
        payload as PrintBlankPayload,
        orderId,
        action,
      )

      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('printBlanc')
    }
  }

  setBarcode = (value: string) => {
    this.barcode = value
  }

  resetBarcode = () => {
    this.barcode = ''
  }

  getProductByBarcode = async (barcode: string) => {
    this.activeLoader('barcode')
    try {
      const { data } = await DeliveryService.getProductByBarcode(barcode)
      return data
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data.error)
      }
    } finally {
      runInAction(() => {
        this.closeLoader('barcode')
      })
    }
  }

  printKitRelabelSticker = async (
    orderId: string,
    payload: KitRelabelStickerPayload,
  ) => {
    try {
      const { data } = await DeliveryService.printKitRelabelSticker(
        orderId,
        payload,
      )
      this.updateKitRelabelState(orderId, data.state)
      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    }
  }

  confirmKitRelabel = async (
    orderId: string,
    payload: KitRelabelConfirmationPayload,
  ) => {
    try {
      const { data } = await DeliveryService.confirmKitRelabel(orderId, payload)
      this.updateKitRelabelState(orderId, data)
      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    }
  }

  updateKitRelabelState = (orderId: string, state: KitRelabelState) => {
    if (this.currentOrderId !== orderId || !this.shippingOrderById) return

    runInAction(() => {
      this.shippingOrderById = {
        ...this.shippingOrderById!,
        detail: {
          ...this.shippingOrderById!.detail,
          kitRelabel: state,
        },
      }
    })
  }

  markKitComponentChecked = (
    kitVariationId: string,
    productVariationId: string,
  ) => {
    const item = this.itemsOrderById.find(
      (candidate) =>
        !candidate.checked &&
        candidate.kitVariationId === kitVariationId &&
        candidate.productVariationId === productVariationId,
    )

    if (!item) return false

    this.setOrderItems(item.id, 'checked', true)
    return true
  }

  setInputmandatoryMark = (value: string) => {
    this.inputMandatoryMark = value
  }

  clearInputmandatoryMark = () => {
    this.inputMandatoryMark = ''
  }

  setOrderItems = (
    id: string,
    name: 'markValue' | 'checked',
    value: boolean | string,
  ) => {
    runInAction(() => {
      this.itemsOrderById = this.itemsOrderById.map((item) =>
        item.id === id ? { ...item, [name]: value } : item,
      )
    })
  }

  checkingItemsOrderById = async (orderId: string) => {
    this.activeLoader('mandatoryMark')
    const payload = {} as Partial<CheckItemsPayload>
    payload.mandatoryMarks = this.itemsOrderById.reduce(
      (acc, item) => {
        if (item.mandatoryMarkField && item.markValue) {
          acc[item.mandatoryMarkField] = item.markValue
        }
        return acc
      },
      {} as { [key: string]: string },
    )
    const payloadKeys = [
      'username',
      'postcode',
      'city',
      'region',
      'address',
      'phone',
      'company',
      'ddp',
      'weight',
      'totalPrice',
    ] as const
    payloadKeys.forEach((key) => {
      if (this.printBlankInfo) {
        const findFormField = [
          ...this.printBlankInfo.additionalFields,
          ...this.printBlankInfo.fields,
        ].find((el) => el.name === key)
        if (findFormField) {
          payload[key] =
            findFormField.value === '' ? null : (findFormField.value as any)
        }
      }
    })
    const finalPayload = payload as CheckItemsPayload
    try {
      await DeliveryService.checkingItemsOrderById(orderId, finalPayload)
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('mandatoryMark')
    }
  }

  validateMarks = async (
    orderId: string,
    payload: { itemId: string; [key: string]: string },
  ) => {
    this.activeLoader('validateMarks')

    try {
      await DeliveryService.validateMarks(orderId, payload)
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('validateMarks')
    }
  }

  reprintMandatoryMark = async (
    orderId: string,
    payload: { itemId: string; identificationCode: string },
  ) => {
    this.activeLoader('mandatoryMarkReprint')

    try {
      const response = await DeliveryService.reprintMandatoryMark(
        orderId,
        payload,
      )
      return response.data
    } finally {
      this.closeLoader('mandatoryMarkReprint')
    }
  }
}

export default DeliveryStore
